import React, {
  ChangeEvent,
  cloneElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Grid,
  makeStyles,
  Tooltip,
  Typography,
} from '@material-ui/core';
import TextField from '@material-ui/core/TextField';
import KeyboardArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import { useDebounce } from 'hooks/debounce-value-hook';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 5,
    borderBottom: '1px solid lightgray',
  },
  leftToolbarContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: '2rem',
  },
  rightToolbarButtons: {
    flexGrow: 1,
    display: 'flex',
    justifyContent: 'flex-end',
    [theme.breakpoints.down('xs')]: {
      order: -1,
    },
  },
  toolbarButtonsWrapper: {
    marginLeft: 'auto',
  },
  slider: {
    maxWidth: 250,
    minWidth: 50,
  },
  buttonWithTooltip: {
    '&.Mui-disabled': {
      pointerEvents: 'auto',
    },
  },
}));

interface Props {
  index: number;
  goToIndex: (index: number) => void;
  itemCount: number;
  handleGoBackward: () => void;
  handleGoForward: () => void;
  goBackwardDisabled?: boolean;
  goForwardDisabled?: boolean;
  handleInfoClick: () => void;
  toolbarButtons?: JSX.Element[];
}

export const ImageViewerToolbar = ({
  handleGoBackward,
  handleGoForward,
  goBackwardDisabled,
  goForwardDisabled,
  toolbarButtons,
  index,
  goToIndex,
  itemCount,
}: Props) => {
  const styles = useStyles();
  const [numberInput, setNumberInput] = useState('');
  const debouncedNumberInput = useDebounce(numberInput, 1000);

  useEffect(() => {
    setNumberInput((index + 1).toString());
  }, [index]);

  useEffect(() => {
    const value = parseInt(debouncedNumberInput);
    if (isNaN(value)) {
      return;
    }

    goToIndex(value - 1);
  }, [debouncedNumberInput, goToIndex]);

  const handleNumberInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => setNumberInput(e.target.value),
    []
  );

  return (
    <Grid
      container
      direction="row"
      alignItems="center"
      justify="space-between"
      className={styles.root}
    >
      <Grid item xs="auto" className={styles.leftToolbarContainer}>
        <Box display="flex" alignItems="center" flexShrink={0}>
          <Typography variant="h6">Stránka:</Typography>
          <TextField
            value={numberInput}
            onChange={handleNumberInputChange}
            style={{ margin: '0 8px' }}
            type="number"
            fullWidth={false}
            variant="standard"
            InputProps={{
              inputProps: {
                min: 1,
                max: itemCount,
                style: {
                  fontSize: 18,
                  padding: 4,
                  marginLeft: 8,
                  textAlign: 'right',
                  color: 'black',
                },
              },
            }}
            placeholder={(index + 1).toString()}
          />
          <Typography variant="h6">/ {itemCount}</Typography>
        </Box>
        <ButtonGroup>
          <Tooltip title="předchozí obrázek" placement="bottom">
            <Button
              component={goBackwardDisabled ? 'div' : 'button'}
              disabled={goBackwardDisabled}
              onClick={handleGoBackward}
              className={styles.buttonWithTooltip}
            >
              <KeyboardArrowLeftIcon />
            </Button>
          </Tooltip>
          <Tooltip title="následující obrázek" placement="bottom">
            <Button
              component={goForwardDisabled ? 'div' : 'button'}
              disabled={goForwardDisabled}
              onClick={handleGoForward}
              className={styles.buttonWithTooltip}
            >
              <KeyboardArrowRightIcon />
            </Button>
          </Tooltip>
        </ButtonGroup>
      </Grid>
      <Grid item className={styles.rightToolbarButtons} xs={12} sm>
        <ButtonGroup>
          {toolbarButtons?.map((button, index) =>
            cloneElement(button, { key: index })
          )}
        </ButtonGroup>
      </Grid>
    </Grid>
  );
};
